import {
  convertEventSelectToTarget,
  FormFeedBack,
  MainButton,
  ModalBase,
  PageWrapper,
  removeCpfMask,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import { CpfInput } from 'adasi_components';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Form,
  Input,
  Label,
  Row,
} from 'reactstrap';

import { useCreateOrUpdateAdministrator } from '../hooks/use-create-or-update-administrator';

export const CreateOrUpdateAdministrator = () => {
  // Hooks
  const {
    id,
    cpfSchema,
    validation,
    showPassword,
    areInputsAndBtnsDisabled,
    createOrUpdateRequestStatus,
    isThereIsAlreadyAnAdminWithThisCPFModalOpen,
    navigate,
    toggleShowPassword,
    requestGetUserById,
    requestGetUserByCpf,
    removeSelectedUserData,
    toggleThereIsAlreadyAnAdminWithThisCPFModal,
  } = useCreateOrUpdateAdministrator();

  useEffect(() => {
    if (id) {
      requestGetUserById({ id });
    }

    return () => {
      removeSelectedUserData();
    };
  }, [id]);

  return (
    <PageWrapper title={`${id ? 'Editar' : 'Novo'} Usuário`}>
      <div className="mb-4">
        <h4>{id ? 'Editar' : 'Novo'} Usuário</h4>
      </div>

      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Card>
          <CardBody>
            <Row className="g-3">
              <Col md={3}>
                <Label htmlFor="cpf" className="form-label">
                  CPF
                </Label>
                <CpfInput
                  name="cpf"
                  inputId="cpf"
                  className="form-control"
                  value={validation.values.cpf}
                  handleChange={(e) => {
                    const { value } = e.target;

                    const isAllowedToRequestGetUserByCpf =
                      value &&
                      !id &&
                      removeCpfMask(value).length === 11 &&
                      cpfSchema.isValidSync(value);

                    if (isAllowedToRequestGetUserByCpf) {
                      requestGetUserByCpf({ cpf: removeCpfMask(value) });
                    }
                    validation.handleChange(e);
                  }}
                  onBlur={validation.handleBlur}
                  disabled={id ? true : areInputsAndBtnsDisabled}
                  invalid={
                    !!(validation?.touched?.cpf && validation?.errors?.cpf)
                  }
                />
                <FormFeedBack
                  error={validation.errors?.cpf}
                  isInvalid={
                    !!(validation?.touched?.cpf && validation.errors?.cpf)
                  }
                />
              </Col>

              <Col md={6}>
                <Label htmlFor="name" className="form-label">
                  Nome completo
                </Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  onBlur={validation.handleBlur}
                  value={validation?.values?.name}
                  onChange={validation.handleChange}
                  disabled={areInputsAndBtnsDisabled}
                  invalid={
                    validation?.touched?.name && !!validation?.errors?.name
                  }
                />
                <FormFeedBack
                  error={validation?.errors?.name}
                  isInvalid={
                    validation?.touched?.name && !!validation?.errors?.name
                  }
                />
              </Col>

              <Col md={3}>
                <Label htmlFor="email" className="form-label">
                  E-mail
                </Label>
                <Input
                  name="email"
                  id="email"
                  type="email"
                  className="form-control"
                  onBlur={validation.handleBlur}
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  disabled={areInputsAndBtnsDisabled}
                  invalid={
                    !!(validation.touched.email && validation.errors?.email)
                  }
                />
                <FormFeedBack
                  error={validation?.errors?.email}
                  isInvalid={
                    validation?.touched?.email && !!validation?.errors?.email
                  }
                />
              </Col>

              <Col md={3}>
                <Label htmlFor="password">
                  {id ? 'Nova senha' : 'Definir senha'}
                </Label>
                <div className="position-relative">
                  <Input
                    name="password"
                    id="password"
                    value={validation.values.password}
                    maxLength={32}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite a senha"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    disabled={areInputsAndBtnsDisabled}
                    invalid={
                      !!(
                        validation.touched.password &&
                        validation.errors.password
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.password}
                    isInvalid={
                      validation?.touched?.password &&
                      !!validation?.errors?.password
                    }
                  />
                  <button
                    className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                    type="button"
                    id="password-addon"
                    disabled={areInputsAndBtnsDisabled}
                    onClick={() => toggleShowPassword()}
                  >
                    <i
                      className={`ri-eye-${showPassword ? 'off-' : ''}fill align-middle`}
                    ></i>
                  </button>
                </div>
              </Col>

              <Col md={3}>
                <Label className="form-label" htmlFor="profile">
                  Perfil
                </Label>
                <Select
                  isClearable
                  id="profile"
                  name="profile"
                  placeholder="Selecione um perfil"
                  onBlur={validation.handleBlur}
                  value={validation.values.profile}
                  options={[
                    {
                      value: PROFILE_TYPE.ADMINISTRATOR,
                      label: 'Administrador',
                    },
                    { value: PROFILE_TYPE.SUPPORT, label: 'Suporte' },
                  ]}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('profile', e),
                    );
                  }}
                  invalid={
                    !!(
                      validation?.touched?.profile &&
                      validation?.errors?.profile
                    )
                  }
                />
                <FormFeedBack
                  error={String(validation.errors?.profile)}
                  isInvalid={
                    !!(
                      validation.touched?.profile && validation.errors?.profile
                    )
                  }
                />
              </Col>
            </Row>
          </CardBody>

          <CardFooter className="d-flex justify-content-end gap-2">
            <Button
              onClick={() => {
                navigate('/cadastros/usuarios');
              }}
              color="link"
              className="text-decoration-underline p-1"
            >
              Voltar
            </Button>
            <MainButton
              type="submit"
              isDisabled={areInputsAndBtnsDisabled}
              isLoading={createOrUpdateRequestStatus === STATUSPAGE.LOADING}
            >
              Salvar
            </MainButton>
          </CardFooter>
        </Card>
      </Form>

      <ModalBase.Wrapper
        loading={false}
        className="text-center"
        isOpen={isThereIsAlreadyAnAdminWithThisCPFModalOpen}
        toggle={toggleThereIsAlreadyAnAdminWithThisCPFModal}
      >
        <ModalBase.Header className="justify-content-center">
          <h3>Atenção</h3>
        </ModalBase.Header>
        <ModalBase.Body>
          <p className="text-muted fw-semibold fs-14 mb-0">
            Já existe um usuário com este CPF
          </p>
        </ModalBase.Body>
        <ModalBase.Footer className="justify-content-center">
          <Button
            color="primary"
            onClick={() => toggleThereIsAlreadyAnAdminWithThisCPFModal()}
            className="mt-2"
          >
            OK
          </Button>
        </ModalBase.Footer>
      </ModalBase.Wrapper>
    </PageWrapper>
  );
};
